import {
  Alert,
  Button,
  ButtonGroup,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import * as Yup from 'yup';

import {
  CompositionBadgeResponseBody,
  CompositionBadgeSize,
  CompositionBadgeStyle,
  useGenerateCompositionBadgeMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {compositionEditorLimits} from '../constants/compositionEditorLimits';
import {notifyBadgeGenerationError} from '../utils/notifyBadgeGenerationError';

type GenerateBadgeFormValues = {
  prompt: string;
  size: CompositionBadgeSize;
  isTransparent: boolean;
  style: CompositionBadgeStyle;
};

interface GenerateBadgeDialogProps extends TestIdProps {
  onGenerated: (badge: CompositionBadgeResponseBody, size: CompositionBadgeSize) => void;
  onCancel: () => void;
}

const SIZE_OPTIONS: {value: CompositionBadgeSize; label: string}[] = [
  {value: '256x256', label: '256 × 256 px'},
  {value: '512x512', label: '512 × 512 px'},
  {value: '1024x1024', label: '1024 × 1024 px'},
];

const STYLE_OPTIONS: {value: CompositionBadgeStyle; label: string}[] = [
  {value: 'badge', label: i18n.t('entity.watermark.composition.ai.labels.styleBadge')},
  {
    value: 'illustration',
    label: i18n.t('entity.watermark.composition.ai.labels.styleIllustration'),
  },
];

const DEFAULT_VALUES: GenerateBadgeFormValues = {
  prompt: '',
  size: '512x512',
  isTransparent: true,
  style: 'badge',
};

const GenerateBadgeFormSchema = Yup.object({
  prompt: Yup.string()
    .trim()
    .required(i18n.t('general.validations.fieldIsRequired'))
    .max(compositionEditorLimits.maxTextLength),
  size: Yup.string().required(),
  isTransparent: Yup.boolean().required(),
  style: Yup.string().required(),
});

// The AI request is sent only from this form's submit; nothing here runs automatically.
export function GenerateBadgeDialog(props: GenerateBadgeDialogProps) {
  const [generateBadge] = useGenerateCompositionBadgeMutation();

  const handleSubmit: FormSubmitHandler<GenerateBadgeFormValues> = (values) =>
    generateBadge({
      generateCompositionBadgeRequestBody: {
        prompt: values.prompt.trim(),
        size: values.size,
        background: values.isTransparent ? 'transparent' : 'opaque',
        style: values.style,
      },
    })
      .unwrap()
      .then((badge) => props.onGenerated(badge, values.size))
      .catch((error: ApiError) => notifyBadgeGenerationError(error));

  return (
    <Form<GenerateBadgeFormValues>
      schema={GenerateBadgeFormSchema}
      defaultValues={DEFAULT_VALUES}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          {/* The generation prompt forbids text, so a wish for a label in the image is never met. */}
          <Alert
            type="inline"
            variant="info"
            message={i18n.t('entity.watermark.composition.ai.labels.noTextHint')}
            data-testid={suffixTestId('noTextHint', props)}
          />
          <FormField
            type="textarea"
            control={control}
            name="prompt"
            label={i18n.t('general.labels.description')}
            helperText={i18n.t('entity.watermark.composition.ai.labels.promptHelper')}
            maxLength={compositionEditorLimits.maxTextLength}
            isCounterVisible
            minRows={3}
            isRequired
            data-testid={suffixTestId('prompt', props)}
          />
          <FormField
            type="choice"
            control={control}
            name="size"
            label={i18n.t('entity.watermark.composition.ai.labels.size')}
            options={SIZE_OPTIONS}
            isNotClearable
            data-testid={suffixTestId('size', props)}
          />
          <FormField
            type="radio"
            control={control}
            name="style"
            label={i18n.t('entity.watermark.composition.ai.labels.style')}
            options={STYLE_OPTIONS}
            data-testid={suffixTestId('style', props)}
          />
          <FormField
            type="switch"
            control={control}
            name="isTransparent"
            label={i18n.t('entity.watermark.composition.ai.labels.transparentBackground')}
            data-testid={suffixTestId('transparentBackground', props)}
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={props.onCancel}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              variant="primary"
              leftIcon="action/magic_wand"
              title={i18n.t('entity.watermark.composition.ai.actions.generate')}
              data-testid={suffixTestId('submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
